import { computed, ref } from 'vue'

import { useLibraryStore } from '@/stores/library'
import type { LibraryItem } from '@/types/library'

/** A picked image from the moment it is read off disk until its library upload settles; the preview is local. */
interface PendingImage {
  id: string
  src: string
  alt: string
  uploadId: string
  item: Promise<LibraryItem | false>
}

/** One image chip, ready to render: local preview plus live upload progress. */
export interface PromptImageAttachment {
  id: string
  src: string
  alt: string
  fraction: number
  uploading: boolean
}

/** What `take()` hands to a submit handler: the preview to show at once and the upload to wait on before the message carries an `itemId`. */
export interface PendingImageHandoff {
  src: string
  alt: string
  result: Promise<LibraryItem | false>
}

function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/** Holds a composer's picked-but-unsent images: local preview, library upload and progress until `take` or removal. */
export function usePromptImageAttachments(options: { max?: number } = {}) {
  const library = useLibraryStore()
  const pending = ref<PendingImage[]>([])

  function uploadFraction(uploadId: string): number {
    const ticket = library.uploads.find((entry) => entry.id === uploadId)
    if (!ticket) return 1
    return ticket.status === 'uploading' ? ticket.fraction : 1
  }

  function isUploading(uploadId: string): boolean {
    return library.uploads.find((entry) => entry.id === uploadId)?.status === 'uploading'
  }

  async function onAttach(files: File[]): Promise<void> {
    const room = options.max ? Math.max(0, options.max - pending.value.length) : files.length
    for (const file of files.slice(0, room)) {
      const src = await readImage(file)
      const upload = library.uploadItem(file)
      pending.value.push({
        id: crypto.randomUUID(),
        src,
        alt: file.name,
        uploadId: upload.id,
        item: upload.result,
      })
    }
  }

  function remove(id: string): void {
    const image = pending.value.find((entry) => entry.id === id)
    if (image) library.cancelUpload(image.uploadId)
    pending.value = pending.value.filter((entry) => entry.id !== id)
  }

  /** Empties the tray without touching the uploads, for a caller that has taken ownership of them. */
  function clear(): void {
    pending.value = []
  }

  /** Discards every picked image, cancelling its upload. */
  function reset(): void {
    for (const image of pending.value) library.cancelUpload(image.uploadId)
    clear()
  }

  const images = computed<PromptImageAttachment[]>(() =>
    pending.value.map((image) => ({
      id: image.id,
      src: image.src,
      alt: image.alt,
      fraction: uploadFraction(image.uploadId),
      uploading: isUploading(image.uploadId),
    })),
  )

  const isUploadingAny = computed(() => pending.value.some((image) => isUploading(image.uploadId)))

  /** Waits for every picked image to land in the library, in submission order; a failed upload is dropped, not blocking. */
  async function settled(): Promise<{ itemId: string; src: string; alt: string }[]> {
    const picked = [...pending.value]
    const items = await Promise.all(picked.map((image) => image.item))
    return items.flatMap((item, index) =>
      item ? [{ itemId: item.id, src: picked[index]!.src, alt: picked[index]!.alt }] : [],
    )
  }

  /** Hands the pending images to a submit handler and clears the tray; ownership of the uploads moves to the caller. */
  function take(): PendingImageHandoff[] {
    const handoff = pending.value.map((image) => ({
      src: image.src,
      alt: image.alt,
      result: image.item,
    }))
    clear()
    return handoff
  }

  return { images, onAttach, remove, reset, isUploadingAny, settled, take }
}
